import { Component, effect, inject, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { ContactView, OpportunityContactView, SearchContactsForOpportunityParameters } from '@api-proxies';
import { IKebabAction } from '@app/components/shared/grids/custom-cell-actions/custom-cell-actions.component';
import { TypeAheadContactsComponent } from '@app/components/shared/type-ahead-contacts/type-ahead-contacts.component';
import { GridService } from '@app/services/grid.service';
import { OpportunityContactService } from '@http/clients/opportunity-contact.service';
import { OpportunityService } from '@http/clients/opportunity.service';
import { ITypeAheadDropdownEntityParameters } from '@interfaces/type-ahead-dropdown-entity-parameters.interface';
import { EntityTypes } from '@models/enums/EntityTypes';
import { OpportunityAction } from '@models/enums/OpportunityAction';
import { AwaiterService } from '@services/awaiter.service';
import { TabCounterService } from '@services/tab-counter.service';
import { ServerSideGridComponent } from '@shared/grids/server-side-grid/server-side-grid.component';
import { ModalConfirmComponent } from '@shared/modals/modal-confirm/modal-confirm.component';
import { ModalEditOpportunityContactComponent } from '@shared/modals/modal-edit-opportunity-contact/modal-edit-opportunity-contact.component';
import { ColDef, IServerSideDatasource } from 'ag-grid-enterprise';
import { ModalService, SnackbarNotificationService } from 'ngx-lib';
import { OPPORTUNITY_CONTACTS_GRID_COLUMNS } from './opportunities-single-contacts.config';

@Component({
    selector: 'app-opportunities-single-contacts',
    templateUrl: './opportunities-single-contacts.component.html',
    styleUrls: ['./opportunities-single-contacts.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [ServerSideGridComponent, TypeAheadContactsComponent]
})
export class OpportunitiesSingleContactsComponent {
    contactsDatasource?: IServerSideDatasource;
    noResults = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    isActionAllowed = signal<boolean>(false);
    gridColumns: ColDef[] = [...OPPORTUNITY_CONTACTS_GRID_COLUMNS];
    typeAheadSearchByEntityParameters: ITypeAheadDropdownEntityParameters = {
        entityKeyName: EntityTypes.Opportunity
    };
    allowSetCompany = true;
    opportunityId?: number;
    listEntitiesUpdated = output<void>();

    private readonly opportunityContactService = inject(OpportunityContactService);
    private readonly opportunityService = inject(OpportunityService);
    private readonly awaiter = inject(AwaiterService);
    private readonly modalService = inject(ModalService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly gridService = inject(GridService);
    private readonly tabCounterService = inject(TabCounterService);
    private availableActions: number[] = [];

    constructor() {
        this.opportunityId = this.opportunityService.opportunity()?.id;

        effect(() => {
            const opportunity = this.opportunityService.opportunity();
            if (opportunity?.id) {
                this.opportunityId = opportunity.id;
                this.availableActions = this.opportunityService.availableActions();
                this.isActionAllowed.set(this.availableActions.includes(OpportunityAction.EditContact));
            }
            if (this.opportunityId && !this.contactsDatasource) {
                this.initializeComponent();
            }
        });
    }

    getRowIdFn = (params: any) => params.data.id.toString();

    async initializeComponent(): Promise<void> {
        if (!this.opportunityId) return;

        this.isActionAllowed.set(this.availableActions.includes(OpportunityAction.EditContact));
        this.setupGridColumns();

        this.typeAheadSearchByEntityParameters.entity = {
            opportunityId: this.opportunityId
        };

        this.createDatasource();
    }

    async onSelectedContact(contact: ContactView): Promise<void> {
        if (!contact || !this.opportunityId) return;

        try {
            const contactSaved = await this.opportunityService.saveContact({
                contactId: contact.id,
                opportunityId: this.opportunityId
            } as any as OpportunityContactView);

            if (contactSaved) {
                const gridApi = this.gridService.getGridApi();

                let updated = false;

                if (gridApi?.applyServerSideTransaction) {
                    const transactionResult = gridApi.applyServerSideTransaction({
                        add: [contactSaved]
                    });

                    updated = transactionResult?.status === 'Applied';
                }

                if (!updated) {
                    this.refreshGridView();
                }

                this.noResults.set(false);
                this.updateOrAddContactInService(contactSaved);
                this.listEntitiesUpdated.emit();
            }
        } catch {
            this.snackbarNotificationService.error('An error occurred while trying to save the contact');
        }
    }

    async editItem(opportunityContact: OpportunityContactView): Promise<void> {
        const opportunityContactUpdated = await this.modalService.open(ModalEditOpportunityContactComponent, {
            contact: opportunityContact
        });

        if (opportunityContactUpdated) {
            const gridApi = this.gridService.getGridApi();
            let updated = false;

            if (gridApi) {
                const node = gridApi.getRowNode(opportunityContactUpdated.id.toString());

                if (node?.data && gridApi.applyServerSideTransaction) {
                    const transactionResult = gridApi.applyServerSideTransaction({
                        update: [opportunityContactUpdated]
                    });
                    updated = transactionResult?.status === 'Applied';
                }
            }

            if (!updated) {
                this.refreshGridView();
            }

            this.updateOrAddContactInService(opportunityContactUpdated);
        }
    }

    async removeItem(opportunityContact: OpportunityContactView): Promise<void> {
        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Remove contact',
            cancelCaption: 'Cancel',
            content: 'Are you sure you want to remove this contact from the contact list?',
            title: 'Remove contact'
        });

        if (!responseOk) return;

        const { id: opportunityContactId } = opportunityContact;
        if (!opportunityContactId) return;

        await this.awaiter.awaitAction(
            'Removing Contact',
            async () => {
                await this.opportunityContactService.delete(opportunityContactId);

                const gridApi = this.gridService.getGridApi();
                let removed = false;

                if (gridApi) {
                    const node = gridApi.getRowNode(opportunityContactId.toString());

                    if (node?.data && gridApi.applyServerSideTransaction) {
                        const transactionResult = gridApi.applyServerSideTransaction({
                            remove: [node.data]
                        });
                        removed = transactionResult?.status === 'Applied';
                    }

                    if (removed) {
                        this.noResults.set(gridApi.getDisplayedRowCount() === 0);
                    }
                }

                if (!removed) {
                    this.refreshGridView();
                }

                this.removeContactFromService(opportunityContactId);
                this.listEntitiesUpdated.emit();
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async updateContactFreezedData(opportunityContact: OpportunityContactView): Promise<void> {
        const modalContent = `Company&emsp;&emsp;${opportunityContact?.companyName ?? '[No data]'} &ensp;>&ensp; ${
            opportunityContact?.currentCompanyName ?? '[No data]'
        }`;

        const responseOk = await this.modalService.open(ModalConfirmComponent, {
            acceptCaption: 'Update Data',
            cancelCaption: 'Cancel',
            bodyTitle: 'The following data will be updated:',
            content: modalContent,
            title: 'Data update available'
        });

        if (!responseOk) return;

        await this.awaiter.awaitAction(
            'Updating contact data',
            async () => {
                const opportunityContactUpdated = await this.opportunityContactService.opportunityContactUpdateCompany(
                    opportunityContact.id
                );

                if (opportunityContactUpdated) {
                    this.snackbarNotificationService.success(
                        `${opportunityContactUpdated.fullName}'s company has been successfully updated.`
                    );

                    const gridApi = this.gridService.getGridApi();
                    let updated = false;

                    if (gridApi) {
                        const node = gridApi.getRowNode(opportunityContactUpdated.id.toString());

                        if (node?.data && gridApi.applyServerSideTransaction) {
                            const transactionResult = gridApi.applyServerSideTransaction({
                                update: [opportunityContactUpdated]
                            });
                            updated = transactionResult?.status === 'Applied';
                        }
                    }

                    if (!updated) {
                        this.refreshGridView();
                    }

                    this.updateOrAddContactInService(opportunityContactUpdated);
                }
            },
            (loading) => this.isLoading.set(loading)
        );
    }

    async refreshGridView() {
        const gridApi = this.gridService.getGridApi();
        if (!gridApi) return;

        gridApi.refreshServerSide({ purge: true });
    }

    private setupGridColumns(): void {
        const actions: IKebabAction[] = [
            {
                label: 'Edit',
                action: (contact: OpportunityContactView) => this.editItem(contact),
                show: () => this.isActionAllowed()
            },
            {
                label: 'Remove',
                action: (contact: OpportunityContactView) => this.removeItem(contact),
                show: () => this.isActionAllowed()
            },
            {
                label: 'Update Data',
                action: (contact: OpportunityContactView) => this.updateContactFreezedData(contact),
                show: (contact: OpportunityContactView) =>
                    this.isActionAllowed() && contact.companyId !== contact.currentCompanyId
            }
        ];

        const baseColumns = this.isActionAllowed()
            ? OPPORTUNITY_CONTACTS_GRID_COLUMNS
            : OPPORTUNITY_CONTACTS_GRID_COLUMNS.filter((col) => col.colId !== 'actions');

        this.gridColumns = baseColumns.map((col) => {
            if (col.colId === 'actions') {
                return {
                    ...col,
                    cellRendererParams: {
                        actions
                    }
                };
            }
            return col;
        });
    }

    private searchParamsBuilder(gridParams: any): SearchContactsForOpportunityParameters {
        const { startRow, sortModel } = gridParams;
        const pageSize = 20;
        const pageNumber = Math.floor(startRow / pageSize) + 1;
        const opportunityId = this.opportunityId ?? 0;

        const searchParams = {
            pageNumber,
            pageSize,
            ...(sortModel?.length && sortModel[0].sort
                ? {
                      sortBy: sortModel[0].colId,
                      sortDirection: sortModel[0].sort === 'asc' ? 'A' : 'D'
                  }
                : {}),
            opportunityId
        } as SearchContactsForOpportunityParameters;

        return searchParams;
    }

    private createDatasource(): void {
        this.contactsDatasource = {
            getRows: async (params: any) => {
                try {
                    this.isLoading.set(true);
                    this.noResults.set(false);

                    const searchParams = this.searchParamsBuilder(params.request);

                    const response = await this.opportunityContactService.search(searchParams);

                    if (response && response.results) {
                        const rowData = response.results || [];
                        const totalCount = response.pageInfo?.itemsCount || 0;

                        this.noResults.set(rowData.length === 0 && params.request.startRow === 0);

                        params.success({
                            rowData: rowData,
                            rowCount: totalCount
                        });
                    } else {
                        console.error('Invalid response structure:', response);
                        params.fail();
                        this.noResults.set(true);
                    }
                } catch (error) {
                    console.error('Error fetching contacts:', error);
                    params.fail();
                    this.noResults.set(true);
                } finally {
                    this.isLoading.set(false);
                }
            }
        };
    }

    private removeContactFromService(contactId: number): void {
        const currentContacts = [...(this.opportunityService.opportunityContacts() || [])];
        const index = currentContacts.findIndex((c) => c.id === contactId);

        if (index !== -1) {
            currentContacts.splice(index, 1);
            this.opportunityService.updateOpportunityContacts(currentContacts);
        }
    }

    private updateOrAddContactInService(contact: OpportunityContactView): void {
        const currentContacts = [...(this.opportunityService.opportunityContacts() || [])];
        const index = currentContacts.findIndex((c) => c.id === contact.id);

        if (index !== -1) {
            currentContacts[index] = contact;
        } else {
            currentContacts.push(contact);
        }

        this.opportunityService.updateOpportunityContacts(currentContacts);
    }
}
